import { MapModuleLayer, StoredBounds, StoredPosition } from "@iventis/map-engine/src/types/store-schema";
import type { History, Location } from "history";
import { MapMode } from "@iventis/map-engine/src/machines/map-machines.types";
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { MapSitemapConfig } from "@iventis/domain-model/model/mapSitemapConfig";
import { timeRange } from "@iventis/utilities";
import { feature } from "@turf/helpers";
import { CompositionMapObject } from "@iventis/map-engine/src/types/internal";
import { SocketEvent, LoadingEvent } from "@iventis/types/loading.types";
import { ResourceTypes, wsSend } from "@iventis/api/src/connection.slice";
import { AnySupportedGeometry, MapObjectProperties, RouteWaypoint, SelectedMapObject } from "@iventis/map-types";
import { DataFieldValueV2 } from "@iventis/domain-model/model/dataFieldValueV2";
import { map } from "./state/map.slice";
import { SliceNames, dispatch, resetSlice, rootStore } from "./state/root.store";
import { sidebar } from "./state/sidebar.slice";
import { searchMapObjectsByArea as searchMapObjectsByAreaApiCall } from "./services/map-objects";
import { routeWaypointService } from "./services/route-waypoint-service";

export const setPosition = (mapPosition: { position: StoredPosition; bounds: StoredBounds }, setLevel: boolean, history: History<unknown>) => {
    if (setLevel) {
        const params = new URLSearchParams(history.location.search);
        const levelFromQueryParams = params.get("level");
        const parsedLevel = Number.parseInt(levelFromQueryParams, 10);
        if (!Number.isNaN(parsedLevel)) {
            // Only update if we have a level in our URL
            rootStore.dispatch(map.actions.updateSelectedLevel(parsedLevel));
        }
    }

    rootStore.dispatch(map.actions.updatePosition(mapPosition));
    rootStore.dispatch(map.actions.updateSitemapsInView());
};

export const setMode = (mode: MapMode) => {
    rootStore.dispatch(map.actions.setMode(mode));
};

export const composeNewObjects = (objectIds: string[]) => {
    dispatch(map.actions.composeNewObject({ objectIds }));
};

/**
 * This function will navigate to the map while updating the library slice with relevant loading state, adding the parentFolderId to the sidebar slice and opening the resource as a MAP to the comms service
 * @param {string} id - id of the map you wish to render
 * @param {string} parentId - id of the map's parent folder
 * @param {History} history - the browser history object
 *
 */
export const navigateToMap = (id: string, parentId: string, history: History) => {
    dispatch(resetSlice(SliceNames.MAP));
    dispatch(wsSend({ eventName: SocketEvent.RESOURCE_OPENED, resourceId: id, sourceId: id, resourceType: ResourceTypes.MAP }));
    dispatch(sidebar.actions.updateParentFolderId(parentId));
    history.push(`/spatial-planner/map/${id}`);
};

export const mapCheckIfWaitingForByEventName = (eventName: SocketEvent | LoadingEvent) => {
    const { waitingFor } = rootStore.getState().mapReducer;
    return waitingFor.some((e) => e.eventName === eventName);
};

export const getSelectedSitemaps = (sitemaps: Sitemap[], sitemapConfigs: MapSitemapConfig[]) =>
    sitemaps.filter((sitemap) => !sitemapConfigs.some((sc) => sc.sitemapId === sitemap.id && sc.version2dVisible === false && sc.version3dVisible === false));

export const getMapIdWithLocation = (location: Location): string => {
    const pathArray = location.pathname.split("/");
    const mapIdIndex = pathArray.findIndex((str) => str === "map") + 1;
    return pathArray[mapIdIndex];
};

/** Searches for map objects in the given bounds, adds the geoJson in the response to the map store and returns the selection */
export const searchMapObjectsByArea = async (
    mapModuleLayers: MapModuleLayer[],
    searchBounds: number[][][],
    mapId: string,
    level: string | number,
    dateFilter?: { day: number; time: number }
) => {
    const layers = mapModuleLayers.filter(({ visible, remote }) => visible && remote);
    const response = (await searchMapObjectsByAreaApiCall(searchBounds, layers, mapId, level, dateFilter))
        // Map objects come down with their source layer IDs, replace that with the map layer ID.
        .map((mapObject) => ({ ...mapObject, layerId: mapModuleLayers.find((layer) => layer.id === mapObject.layerId).id }));

    // Assign their waypoints if they are routes
    const objectIdToWaypointsMapping: Record<string, RouteWaypoint[]> = {};
    for (let i = 0; i < response.length; i += 1) {
        const object = response[i];
        if (object.modeOfTransport != null) {
            objectIdToWaypointsMapping[object.id] = await routeWaypointService.getRouteWaypoints(object.id, mapId);
        }
    }

    dispatch(
        map.actions.setLocalToRemoteMapObjectsKey(
            response.reduce((cum, object) => {
                if (!Object.prototype.hasOwnProperty.call(rootStore.getState().mapReducer.mapModule.localToRemoteMapObjectIdsMap.value, object.id)) {
                    return [...cum, { key: object.id, value: object.id }];
                }
                return cum;
            }, [])
        )
    );

    const newLocalGeoJson = response.map(
        (mapObject) =>
            ({
                geojson: feature(
                    mapObject.geoJsonFeature.geometry,
                    {
                        ...mapObject.geoJsonFeature.properties,
                        layerid: mapObject.layerId,
                        id: mapObject.id,
                        fixedShape: mapObject.fixedShape,
                        ...dataFieldValuesToProperties(mapObject.dataFieldValues),
                    },
                    { id: mapObject.id }
                ),
                objectId: mapObject.id,
                layerId: mapObject.layerId,
                waypoints: objectIdToWaypointsMapping[mapObject.id],
            } as CompositionMapObject)
    );

    dispatch(map.actions.setLocalGeoJson(newLocalGeoJson));

    const selectedMapObjects: SelectedMapObject[] = response.map((mapObject) => ({
        layerId: mapObject.layerId,
        objectId: mapObject.id,
    }));

    return { selectedMapObjects } as const;
};

/** Deconstructs DF values and changes DF list item values to be represented by their Id */
export const dataFieldValuesToProperties = (dataFieldValues: DataFieldValueV2[]) => {
    if (!dataFieldValues || !Array.isArray(dataFieldValues)) return {};

    const mapDataFields = {};

    dataFieldValues.forEach((dfValue) => {
        if (dfValue.valueTickbox) {
            mapDataFields[dfValue.dataFieldId] = dfValue.valueTickbox;
        } else if (dfValue.valueNumber) {
            mapDataFields[dfValue.dataFieldId] = dfValue.valueNumber;
        } else if (dfValue.valueId) {
            mapDataFields[dfValue.dataFieldId] = dfValue.valueId;
        } else if (dfValue.valueDate) {
            mapDataFields[dfValue.dataFieldId] = dfValue.valueDate;
        } else if (dfValue.valueTime) {
            mapDataFields[dfValue.dataFieldId] = dfValue.valueTime;
        } else if (dfValue.valueTimeRangeFrom || dfValue.valueTimeRangeTo) {
            mapDataFields[dfValue.dataFieldId] = timeRange(dfValue.valueTimeRangeFrom, dfValue.valueTimeRangeTo);
        } else if (dfValue.valueRepeatedTimeRanges && dfValue.valueRepeatedTimeRanges.length > 0) {
            mapDataFields[dfValue.dataFieldId] = dfValue.valueRepeatedTimeRanges;
        } else if (dfValue.listItemId) {
            mapDataFields[dfValue.dataFieldId] = dfValue.listItemId;
        } else {
            // Default to text value
            mapDataFields[dfValue.dataFieldId] = dfValue.valueText;
        }
    });

    return mapDataFields;
};

/**
 * Creates a map object with geojson
 */
export const createMapObjectWithGeoJson = (properties: MapObjectProperties, geometry: AnySupportedGeometry, waypoints: RouteWaypoint[]): CompositionMapObject => ({
    layerId: properties.layerid,
    objectId: properties.id,
    geojson: {
        type: "Feature",
        geometry,
        id: properties.id,
        properties,
    },
    waypoints,
});
